import BoltOnRenewalSummary from '../components/BoltOnRenewalSummary';
// components/teams/detailCards/boltOns.jsx
import { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase-client';
import { useAuth } from '../../../auth/AuthContext';
import EditButton from '../../ui/EditButton';
import BoltOnPurchaseModal from '../components/modals/boltOnPurchaseModal';
import BoltOnManageModal from '../components/modals/boltOnManageModal';
import BoltOnPurchasersModal from '../components/modals/BoltOnPurchasersModal';
import { getCurrencyMeta } from '../../../utils/currency';
import { useOnlineRefreshSignal } from '../../../utils/onlineStatus';
function formatWhen(d) {
  if (!d) return null;
  try {
    let date;
    if (typeof d?.toDate === 'function') date = d.toDate();else if (typeof d === 'number') date = new Date(d);else date = new Date(String(d));
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return null;
  }
}
function isPendingCancellation(entry) {
  if (!entry) return false;
  return entry.status === 'cancel_at_period_end' || entry.cancelAtPeriodEnd === true || entry.manualRenewalsStopped === true || entry.autoRenew === false;
}
function formatEntryStatus(entry) {
  if (!entry) return 'None';
  const mode = entry.mode;
  if (mode === 'subscription') {
    if (entry.status === 'active') return 'Active (subscription)';
    if (entry.status === 'cancel_at_period_end' || entry.cancelAtPeriodEnd) {
      return 'Cancels at period end';
    }
    return 'Inactive';
  }
  if (mode === 'manual') {
    if (entry.manualRenewalsStopped || entry.autoRenew === false || entry.status === 'cancel_at_period_end') {
      return 'Cancels at period end';
    }
    if (entry.status === 'active') return 'Active (manual)';
    return 'Inactive';
  }
  return 'Unknown';
}
function formatQuantity(entry) {
  const quantity = Math.max(1, Number(entry?.quantity || 1));
  return quantity > 1 ? `Qty ${quantity}` : null;
}

function BoltOnsCard({
  onEdit,
  canEdit = false,
  teamId,
  refreshKey,
  memberProfiles = []
}) {
  const {
    user
  } = useAuth();
  const [boltOns, setBoltOns] = useState(null);
  const [myBoltOns, setMyBoltOns] = useState({}); // { [boltOnId]: { status, mode, ... } }
  const [purchaseTarget, setPurchaseTarget] = useState(null);
  const [manageTarget, setManageTarget] = useState(null);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [internalTick, setInternalTick] = useState(0); // bump after modal closes to reload
  const [currency, setCurrency] = useState('GBP');
  const [currencyLocked, setCurrencyLocked] = useState(false);
  const [stripeReady, setStripeReady] = useState(null);
  const onlineRefreshSignal = useOnlineRefreshSignal();

  useEffect(() => {
    const loadBoltOns = async () => {
      if (!teamId) return;
      try {
        const setupSnap = await getDoc(doc(db, 'teams', teamId, 'stripe', 'setup'));
        if (setupSnap.exists()) {
          const setup = setupSnap.data() || {};
          setCurrency(setup.currency || 'GBP');
          setCurrencyLocked(Boolean(setup.currencyLocked));
          setStripeReady(setup.onboardingCompleted === true && setup.chargesEnabled === true && setup.payoutsEnabled === true && setup.transfersEnabled === true);
        } else {
          setStripeReady(false);
        }
        const snap = await getDocs(collection(db, 'teams', teamId, 'boltOns'));
        const items = snap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        })).filter(boltOn => boltOn.hiddenFromBoltOns !== true && boltOn.source !== 'moorings');
        setBoltOns(items);
      } catch (err) {
        // Retain the last successful catalogue during a transient read failure.
      }
    };
    loadBoltOns();
  }, [teamId, refreshKey, internalTick, onlineRefreshSignal]);
  useEffect(() => {
    const loadMine = async () => {
      if (!user?.uid || !teamId) return;
      try {
        const ref = collection(db, 'teams', teamId, 'members', user.uid, 'boltOns');
        const snap = await getDocs(ref);
        const map = {};
        snap.forEach(d => {
          map[d.id] = d.data();
        });
        setMyBoltOns(map);
      } catch (e) {
        // Retain known purchases so a failed refresh cannot hide active add-ons.
      }
    };
    loadMine();
  }, [user?.uid, teamId, refreshKey, internalTick, onlineRefreshSignal]);
  const canManage = boltOnId => !!myBoltOns[boltOnId];

  const renderBoltOnRow = boltOn => {
    const myEntry = myBoltOns[boltOn.id];
    const activeForUser = myEntry?.status === 'active';
    const manageable = canManage(boltOn.id);
    const pending = isPendingCancellation(myEntry);
    const {
      symbol,
      decimals
    } = getCurrencyMeta(currency);
    const fmt = minor => `${symbol}${(Number(minor || 0) / Math.pow(10, decimals)).toFixed(decimals)}`;
    const endDate = formatWhen(myEntry?.paidThrough) || formatWhen(myEntry?.currentPeriodEnd) || formatWhen(myEntry?.nextChargeDate) || formatWhen(myEntry?.renewalDate) || null;
    const quantityLabel = formatQuantity(myEntry);
    const quantityLimit = Number.isFinite(Number(boltOn?.checkoutQuantityLimit)) && Number(boltOn.checkoutQuantityLimit) > 0 ? Math.floor(Number(boltOn.checkoutQuantityLimit)) : null;
    const totalLimit = Number.isFinite(Number(boltOn?.totalQuantityLimit)) && Number(boltOn.totalQuantityLimit) > 0 ? Math.floor(Number(boltOn.totalQuantityLimit)) : null;

    // Primary action render logic
    let action = null;
    if (!boltOn.active) {
      action = manageable ? <button className="btn btn--secondary btn--sm" onClick={() => setManageTarget(boltOn)}>
          Manage
        </button> : <span className="text-muted" style={{
        fontSize: '0.9em'
      }}>
          Not for sale
        </span>;
    } else {
      if (manageable) {
        if (pending) {
          const label = myEntry?.mode === 'subscription' ? 'Cancelling at period end' : 'Manual renewals stopped';
          action = <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
              <button className="btn btn--secondary btn--sm" disabled aria-disabled="true" title={endDate ? `Ends on ${endDate}` : undefined} style={{
              opacity: 0.6,
              cursor: 'not-allowed'
            }}>
                {label}
              </button>
              <button className="btn btn--ghost btn--sm" onClick={() => setManageTarget(boltOn)}>
                Manage
              </button>
            </div>;
        } else if (activeForUser) {
          action = <button className="btn btn--secondary btn--sm" onClick={() => setManageTarget(boltOn)}>
              Manage
            </button>;
        } else {
          // Has an entry but not active (edge cases) – allow Manage
          action = <button className="btn btn--secondary btn--sm" onClick={() => setManageTarget(boltOn)}>
              Manage
            </button>;
        }
      } else {
        // Not owned by user → offer Buy
        action = stripeReady === false
          ? <span className="text-muted" style={{ fontSize: '0.9em' }}>Payments temporarily unavailable</span>
          : <button className="btn btn--primary btn--sm" disabled={stripeReady == null} onClick={() => setPurchaseTarget(boltOn)}>
              {stripeReady == null ? 'Checking payments…' : 'Buy'}
            </button>;
      }
    }
    return <div key={boltOn.id} style={{
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      justifyContent: 'space-between',
      flexWrap: 'wrap'
    }}>
        <div style={{
        flex: '1 1 16rem', minWidth: 0
      }}>
          <strong>{boltOn.name}</strong> — {fmt(boltOn.amount)}
          {boltOn.renewalEvery && boltOn.renewalUnit && <> (Every {boltOn.renewalEvery} {boltOn.renewalUnit}
              {boltOn.renewalEvery > 1 ? 's' : ''})
            </>}
          {!boltOn.active && <span style={{
          color: 'red',
          marginLeft: '0.5rem'
        }}>(Inactive)</span>}
          {quantityLimit && <span style={{
          marginLeft: '0.5rem',
          color: '#666'
        }}>
              Max {quantityLimit} per checkout
            </span>}
          {totalLimit && <span style={{
          marginLeft: '0.5rem',
          color: '#666'
        }}>
              Total allowed {totalLimit}
            </span>}
          {manageable && <span style={{
          marginLeft: '0.5rem',
          color: '#666'
        }}>
    — {formatEntryStatus(myEntry)}
    {quantityLabel ? ` • ${quantityLabel}` : ''}
    {Number(myEntry?.membershipYearCreditMinor || 0) > 0 ? ` • ${fmt(myEntry.membershipYearCreditMinor)} alignment credit` : ''}
    {pending && <>
        {' • '}
        <span className="text-muted">
          {myEntry.mode === 'subscription' ? 'Ends' : 'Access until'}
          {endDate ? ` ${endDate}` : ' at period end'}
        </span>
      </>}
  </span>}
          {manageable && <BoltOnRenewalSummary entry={myEntry} currency={currency} />}
        </div>

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.35rem'
      }}>
          {action}
        </div>
      </div>;
  };

  // after closing a modal, bump internalTick to force a quick refetch
  const handleClosePurchase = () => {
    setPurchaseTarget(null);
    setInternalTick(t => t + 1);
  };
  const handleCloseManage = () => {
    setManageTarget(null);
    setInternalTick(t => t + 1);
  };
  const handleCloseInsights = () => {
    setInsightsOpen(false);
  };
  const showInsightsButton = canEdit && Array.isArray(boltOns) && boltOns.length > 0;
  return <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Bolt-ons</span>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
          {showInsightsButton && <button type="button" onClick={() => setInsightsOpen(true)} aria-label="View bolt-on purchaser insights" title="View bolt-on purchaser insights" className="edit-btn">
              <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2a8 8 0 105.293 14.293l5.207 5.207a1 1 0 001.414-1.414l-5.207-5.207A8 8 0 0010 2zm0 2a6 6 0 110 12 6 6 0 010-12z" />
              </svg>
            </button>}
          <EditButton section="boltOns" onEdit={canEdit ? onEdit : null} />
        </div>
      </div>

      <div className="card-body">
        <p className="text-muted text-sm" style={{
        marginBottom: '0.5rem'
      }}>
          Currency: {currency}{currencyLocked ? ' (locked after first payment)' : ''}
        </p>
        {!boltOns ? <p className="text-muted">Loading bolt-ons…</p> : boltOns.length === 0 ? <p className="text-muted">No bolt-ons configured.</p> : <div className="bolt-on-list text-sm text-muted">
            {boltOns.map(renderBoltOnRow)}
          </div>}
      </div>

      {purchaseTarget && <BoltOnPurchaseModal teamId={teamId} boltOn={purchaseTarget} onClose={handleClosePurchase} />}
      {manageTarget && <BoltOnManageModal teamId={teamId} boltOn={manageTarget} myEntry={myBoltOns[manageTarget.id]} currency={currency} onClose={handleCloseManage} />}
      {insightsOpen && <BoltOnPurchasersModal isOpen={insightsOpen} onClose={handleCloseInsights} teamId={teamId} boltOns={Array.isArray(boltOns) ? boltOns : []} currency={currency} memberProfiles={memberProfiles} />}
    </div>;
}
export default BoltOnsCard;
